import { MessageIcon, PhoneIcon } from "./Icon.tsx";
import { decodeEntities } from "../lib/decode-entities.ts";

interface Appointment {
  id: string;
  start_at: string;
  end_at: string;
  status: "confirmed" | "tentative" | "completed" | "no_show" | "cancelled";
  customer_name: string;
  customer_phone: string;
  service_name: string;
  staff_name: string;
  notes?: string | null;
}

interface Props {
  appointment: Appointment;
  onAction?: (id: string, status: string) => void;
  loading?: boolean;
}

const STATUS_LABEL: Record<Appointment["status"], string> = {
  confirmed: "Confirmed",
  tentative: "Tentative",
  completed: "Complete",
  no_show: "No-show",
  cancelled: "Cancelled",
};

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function durationMinutes(start: string, end: string) {
  return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AppointmentCard({ appointment: appt, onAction, loading }: Props) {
  const dur = durationMinutes(appt.start_at, appt.end_at);
  const phoneClean = appt.customer_phone?.replace(/\D/g, "") ?? "";
  const waLink = phoneClean ? `https://wa.me/${phoneClean}` : null;
  const telLink = phoneClean ? `tel:${appt.customer_phone}` : null;

  return (
    <article className="zd-appt" data-status={appt.status}>
      <div className="zd-appt-time">
        <div className="t">{formatTime(appt.start_at)}</div>
        <div className="end">→ {formatTime(appt.end_at)} · {dur} min</div>
      </div>

      <div className="zd-appt-avatar" data-status={appt.status} aria-hidden="true">
        {initials(decodeEntities(appt.customer_name))}
      </div>

      <div className="zd-appt-body">
        <div className="name">{decodeEntities(appt.customer_name)}</div>
        <div className="meta">
          {decodeEntities(appt.service_name)}
          {appt.staff_name && <><span className="sep">·</span>{appt.staff_name}</>}
        </div>
        {appt.notes && <div className="note">{appt.notes}</div>}
      </div>

      <div className="zd-appt-actions">
        {telLink && (
          <a href={telLink} className="zd-appt-icon" title={`Call ${decodeEntities(appt.customer_name)}`} aria-label="Call">
            <PhoneIcon size={14} />
          </a>
        )}
        {waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="zd-appt-icon"
            title={`Message ${decodeEntities(appt.customer_name)}`}
            aria-label="Message"
          >
            <MessageIcon size={14} />
          </a>
        )}
        {appt.status === "confirmed" && onAction ? (
          <>
            <button
              className="zd-ghost-btn primary"
              disabled={loading}
              onClick={() => onAction(appt.id, "completed")}
            >
              Mark complete
            </button>
            <button
              className="zd-ghost-btn"
              disabled={loading}
              onClick={() => onAction(appt.id, "no_show")}
            >
              No-show
            </button>
          </>
        ) : (
          <span className="zd-appt-status">{STATUS_LABEL[appt.status]}</span>
        )}
      </div>
    </article>
  );
}
